import * as React from 'react';
import { Check, ChevronsUpDown, Triangle, X } from 'lucide-react';
import {
    Command,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/ui/design-system/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/ui/design-system/components/ui/popover';
import { Badge } from '@/ui/design-system/components/ui/badge';
import { Button } from '@/ui/design-system/components/ui/button';
import { Label } from '@/ui/design-system/components/ui/label';
import { cn } from '@/ui/design-system/utils/cn';

const groupCategoriesByParent = (categories) => {
    if (!categories) return {};

    const groups = {};

    categories.forEach((category) => {
        // Only group cat3 categories - cat2 categories are not available for selection
        if (category.depth === 'cat3') {
            const groupLabel = category.cat2_label;
            if (!groups[groupLabel]) {
                groups[groupLabel] = [];
            }
            groups[groupLabel].push(category);
        }
    });

    return groups;
};

const CategoryMultiSelect = React.forwardRef(
    (
        {
            className,
            label = 'Categories',
            description,
            placeholder = 'Select categories...',
            searchPlaceholder = 'Search categories...',
            emptyText = 'No categories found.',
            categories = [],
            isLoading = false,
            error = null,
            value = [],
            onChange,
            disabled = false,
            maxItems,
            ...props
        },
        ref,
    ) => {
        const [open, setOpen] = React.useState(false);
        const [searchValue, setSearchValue] = React.useState('');
        const [expandedGroups, setExpandedGroups] = React.useState({});
        const groupedCategories = React.useMemo(
            () => groupCategoriesByParent(categories),
            [categories],
        );
        const allOptions = React.useMemo(() => {
            return Object.values(groupedCategories).flat();
        }, [groupedCategories]);

        // Filter groups based on search - show entire group if group name matches
        const filteredGroups = React.useMemo(() => {
            if (!searchValue) return groupedCategories;

            const searchLower = searchValue.toLowerCase();
            const filtered = {};

            Object.entries(groupedCategories).forEach(
                ([groupLabel, options]) => {
                    // Check if group label matches
                    const groupMatches = groupLabel
                        .toLowerCase()
                        .includes(searchLower);

                    if (groupMatches) {
                        // Show entire group
                        filtered[groupLabel] = options;
                    } else {
                        // Only show items that match
                        const matchingOptions = options.filter((opt) =>
                            opt.name.toLowerCase().includes(searchLower),
                        );
                        if (matchingOptions.length > 0) {
                            filtered[groupLabel] = matchingOptions;
                        }
                    }
                },
            );

            return filtered;
        }, [groupedCategories, searchValue]);

        const toggleGroup = (groupLabel) => {
            setExpandedGroups((prev) => ({
                ...prev,
                [groupLabel]: !prev[groupLabel],
            }));
        };

        const handleGroupSelect = (groupLabel) => {
            const groupCategories = groupedCategories[groupLabel] || [];
            if (groupCategories.length === 0) return;

            const parentId = groupCategories[0].parent_id;
            const isGroupSelected = value.some((v) => v.cat2 === groupLabel);

            let newValue;
            if (isGroupSelected) {
                // Deselect entire group - remove the cat2
                newValue = value.filter((v) => v.cat2 !== groupLabel);
            } else {
                // Check if any individual cat3s from this group are selected
                const selectedCat3Ids = value
                    .filter((v) =>
                        groupCategories.some((cat) => cat.id === v.id),
                    )
                    .map((v) => v.id);

                // Remove any individual cat3s from this group
                newValue = value.filter((v) => !selectedCat3Ids.includes(v.id));

                // Add the entire group as cat2
                newValue.push({
                    id: parentId,
                    cat2: groupLabel,
                });
            }

            onChange?.(newValue);
        };

        const handleSelect = (category) => {
            const isDirectlySelected = value.some((v) => v.id === category.id);
            const parentCat2 = value.find(
                (v) => v.cat2 === category.cat2_label,
            );

            let newValue;
            if (isDirectlySelected) {
                // Deselecting - just remove it
                newValue = value.filter((v) => v.id !== category.id);
            } else if (parentCat2) {
                // The parent cat2 is selected, user is deselecting this specific cat3
                // Expand the cat2 to individual cat3s minus this one
                const groupLabel = category.cat2_label;
                const groupCategories = groupedCategories[groupLabel] || [];

                newValue = value.filter((v) => v.id !== parentCat2.id);

                // Add all cat3s from this group except the one being deselected
                groupCategories.forEach((cat) => {
                    if (cat.id !== category.id) {
                        newValue.push({ id: cat.id, cat3: cat.name });
                    }
                });
            } else {
                // Selecting - check if we should enforce maxItems
                if (maxItems && value.length >= maxItems) {
                    newValue = value;
                } else {
                    newValue = [
                        ...value,
                        { id: category.id, [category.depth]: category.name },
                    ];

                    // Check if all cat3s in this cat2 group are now selected
                    const groupLabel = category.cat2_label;
                    const groupCategories = groupedCategories[groupLabel] || [];
                    const allInGroupSelected = groupCategories.every((cat) =>
                        newValue.some((v) => v.id === cat.id),
                    );

                    if (allInGroupSelected && groupCategories.length > 0) {
                        // Replace all cat3s with a single cat2
                        // Remove all cat3s from this group
                        newValue = newValue.filter(
                            (v) =>
                                !groupCategories.some((cat) => cat.id === v.id),
                        );
                        // Add the cat2 (use parent_id from any category in the group)
                        const parentId = groupCategories[0].parent_id;
                        newValue.push({
                            id: parentId,
                            cat2: groupLabel,
                        });
                    }
                }
            }

            onChange?.(newValue);
        };

        const handleRemove = React.useCallback(
            (categoryId, e) => {
                e.stopPropagation();

                // Check if this is a cat2 being removed
                const categoryToRemove = value.find((v) => v.id === categoryId);
                if (categoryToRemove && categoryToRemove.cat2) {
                    // It's a cat2 - just remove it entirely
                    onChange?.(value.filter((v) => v.id !== categoryId));
                } else {
                    // It's a cat3 - check if its parent cat2 is selected
                    const parentCat2 = value.find(
                        (v) =>
                            v.cat2 &&
                            Object.values(groupedCategories).some((group) =>
                                group.some(
                                    (cat) =>
                                        cat.id === categoryId &&
                                        cat.cat2_label === v.cat2,
                                ),
                            ),
                    );

                    if (parentCat2) {
                        // Parent cat2 is selected - expand it to individual cat3s minus the one being removed
                        const groupLabel = parentCat2.cat2;
                        const groupCategories =
                            groupedCategories[groupLabel] || [];

                        const newValue = value.filter(
                            (v) => v.id !== parentCat2.id,
                        );

                        // Add all cat3s from this group except the one being removed
                        groupCategories.forEach((cat) => {
                            if (cat.id !== categoryId) {
                                newValue.push({ id: cat.id, cat3: cat.name });
                            }
                        });

                        onChange?.(newValue);
                    } else {
                        // Normal removal
                        onChange?.(value.filter((v) => v.id !== categoryId));
                    }
                }
            },
            [onChange, value, groupedCategories],
        );

        // For selected values, try to find in available options first
        // If not found (e.g., legacy cat2 categories), display them but they won't be in the menu
        const selectedOptions = value.map((selectedCat) => {
            const found = allOptions.find((opt) => opt.id === selectedCat.id);
            if (found) return found;

            // Handle legacy cat2 categories that aren't in the available options
            return {
                id: selectedCat.id,
                name: selectedCat.cat2 || selectedCat.cat3,
                depth: selectedCat.cat2 ? 'cat2' : 'cat3',
            };
        });

        const displayValue = React.useMemo(() => {
            if (selectedOptions.length === 0) {
                return (
                    <span className="text-muted-foreground">{placeholder}</span>
                );
            }

            return (
                <div className="flex gap-1 items-center flex-wrap">
                    {selectedOptions.map((option) => (
                        <Badge
                            key={option.id}
                            variant="secondary"
                            interactive
                            className="h-6 px-1.5"
                            onClick={(e) => handleRemove(option.id, e)}
                        >
                            <span className="text-xs capitalize whitespace-nowrap">
                                {option.name}
                            </span>
                            <X className="h-3 w-3 text-muted-foreground" />
                        </Badge>
                    ))}
                </div>
            );
        }, [selectedOptions, placeholder, handleRemove]);

        // Show loading state
        if (isLoading) {
            return (
                <div className="flex flex-col gap-2 w-full">
                    {label && (
                        // eslint-disable-next-line jsx-a11y/label-has-for
                        <Label className="text-sm font-medium text-foreground">
                            {label}
                        </Label>
                    )}
                    <Button
                        variant="outline"
                        disabled
                        className="w-full h-9 justify-between font-normal"
                    >
                        <span className="text-muted-foreground">
                            Loading categories...
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </div>
            );
        }

        // Show error state
        if (error) {
            return (
                <div className="flex flex-col gap-2 w-full">
                    {label && (
                        // eslint-disable-next-line jsx-a11y/label-has-for
                        <Label className="text-sm font-medium text-foreground">
                            {label}
                        </Label>
                    )}
                    <Button
                        variant="outline"
                        disabled
                        className="w-full h-9 justify-between font-normal"
                    >
                        <span className="text-destructive">
                            Error loading categories
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </div>
            );
        }

        return (
            <div className="flex flex-col gap-2 w-full">
                {label && (
                    <Label
                        htmlFor={`category-multiselect-${label}`}
                        className="text-sm font-medium text-foreground"
                    >
                        {label}
                    </Label>
                )}
                <Popover open={open} onOpenChange={setOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            id={
                                label
                                    ? `category-multiselect-${label}`
                                    : undefined
                            }
                            ref={ref}
                            variant="outline"
                            role="combobox"
                            aria-expanded={open}
                            aria-haspopup="listbox"
                            disabled={disabled}
                            className={cn(
                                'w-full h-auto min-h-9 justify-between font-normal bg-background border-input px-4 py-2 text-sm',
                                'hover:bg-background hover:border-input',
                                'focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-0',
                                !value.length && 'text-muted-foreground',
                                className,
                            )}
                            {...props}
                        >
                            <div className="flex-1 text-left">
                                {displayValue}
                            </div>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 self-start mt-0.5" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        className="w-[350px] p-0 border-border bg-popover"
                        align="start"
                    >
                        <Command
                            className="rounded-md bg-popover"
                            shouldFilter={false}
                        >
                            <CommandInput
                                placeholder={searchPlaceholder}
                                className="h-9 text-sm"
                                value={searchValue}
                                onValueChange={setSearchValue}
                            />
                            <CommandList className="max-h-[300px]">
                                {Object.keys(filteredGroups).length === 0 ? (
                                    <div className="py-3 text-center text-sm text-muted-foreground">
                                        {emptyText}
                                    </div>
                                ) : null}
                                {Object.entries(filteredGroups).map(
                                    ([groupLabel, options]) => {
                                        const isGroupSelected = value.some(
                                            (v) => v.cat2 === groupLabel,
                                        );
                                        const selectedCat3Count =
                                            options.filter((opt) =>
                                                value.some(
                                                    (v) => v.id === opt.id,
                                                ),
                                            ).length;
                                        const isIndeterminate =
                                            !isGroupSelected &&
                                            selectedCat3Count > 0;
                                        // Auto-expand when searching, otherwise use manual expand state
                                        const isExpanded =
                                            searchValue ||
                                            expandedGroups[groupLabel];

                                        return (
                                            <CommandGroup key={groupLabel}>
                                                {/* Custom group header with checkbox and triangle */}
                                                <div className="flex items-center gap-2 px-2 py-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleGroup(
                                                                groupLabel,
                                                            );
                                                        }}
                                                        className="flex items-center justify-center h-4 w-4 shrink-0"
                                                    >
                                                        <Triangle
                                                            className={cn(
                                                                'h-2.5 w-2.5 fill-current text-muted-foreground transition-transform transform',
                                                                {
                                                                    'rotate-180':
                                                                        isExpanded,
                                                                    'rotate-90':
                                                                        !isExpanded,
                                                                },
                                                            )}
                                                        />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleGroupSelect(
                                                                groupLabel,
                                                            )
                                                        }
                                                        className="flex items-center gap-2"
                                                    >
                                                        <div
                                                            className={cn(
                                                                'h-4 w-4 rounded-[4px] border flex items-center justify-center transition-colors',
                                                                isGroupSelected ||
                                                                    isIndeterminate
                                                                    ? 'bg-primary border-primary'
                                                                    : 'border-input',
                                                            )}
                                                        >
                                                            {isGroupSelected && (
                                                                <Check className="h-3 w-3 text-primary-foreground" />
                                                            )}
                                                            {isIndeterminate && (
                                                                <svg
                                                                    className="h-3 w-3 text-primary-foreground"
                                                                    viewBox="0 0 15 15"
                                                                    fill="none"
                                                                >
                                                                    <rect
                                                                        x="3"
                                                                        y="7"
                                                                        width="9"
                                                                        height="1"
                                                                        fill="currentColor"
                                                                    />
                                                                </svg>
                                                            )}
                                                        </div>
                                                        <span className="text-sm font-medium capitalize">
                                                            {groupLabel}
                                                        </span>
                                                    </button>
                                                </div>

                                                {/* Cat3 items - only show when expanded */}
                                                {isExpanded &&
                                                    options.map((option) => {
                                                        // Check if this specific cat3 is selected OR if its parent cat2 is selected
                                                        const isDirectlySelected =
                                                            value.some(
                                                                (v) =>
                                                                    v.id ===
                                                                    option.id,
                                                            );
                                                        const isParentSelected =
                                                            value.some(
                                                                (v) =>
                                                                    v.cat2 ===
                                                                    option.cat2_label,
                                                            );
                                                        const isSelected =
                                                            isDirectlySelected ||
                                                            isParentSelected;

                                                        const isDisabled =
                                                            maxItems &&
                                                            value.length >=
                                                                maxItems &&
                                                            !isSelected;

                                                        return (
                                                            <CommandItem
                                                                key={option.id}
                                                                value={`${option.name} ${groupLabel}`}
                                                                keywords={[
                                                                    option.name,
                                                                    groupLabel,
                                                                ]}
                                                                disabled={
                                                                    isDisabled
                                                                }
                                                                onSelect={() => {
                                                                    handleSelect(
                                                                        option,
                                                                    );
                                                                }}
                                                                className={cn(
                                                                    'group flex items-center gap-2 pl-8 pr-2 py-1 text-sm rounded-sm cursor-pointer transition-colors',
                                                                    isDisabled &&
                                                                        'opacity-50 cursor-not-allowed',
                                                                )}
                                                            >
                                                                <div
                                                                    className={cn(
                                                                        'h-4 w-4 rounded-[4px] border flex items-center justify-center transition-colors',
                                                                        isSelected
                                                                            ? 'bg-primary border-primary'
                                                                            : 'border-input group-data-[selected=true]:border-muted-foreground/70',
                                                                    )}
                                                                >
                                                                    {isSelected && (
                                                                        <Check className="h-3 w-3 text-primary-foreground" />
                                                                    )}
                                                                </div>
                                                                <span
                                                                    className={cn(
                                                                        'capitalize',
                                                                        isSelected &&
                                                                            'font-medium',
                                                                    )}
                                                                >
                                                                    {
                                                                        option.name
                                                                    }
                                                                </span>
                                                            </CommandItem>
                                                        );
                                                    })}
                                            </CommandGroup>
                                        );
                                    },
                                )}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
                {description && (
                    <p className="text-sm text-muted-foreground">
                        {description}
                    </p>
                )}
            </div>
        );
    },
);

CategoryMultiSelect.displayName = 'CategoryMultiSelect';

export default CategoryMultiSelect;
